import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";

import {
    CalendarDays,
    Clock,
    Users,
    Settings,
    Ban,
    Loader2,
    Check,
    X,
    UserCheck,
    UserX,
    Trash2,
    Plus,
    ExternalLink,
    Copy,
    Mail,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { ReservationSettings, ReservationWithTables, ReservationBlackoutDate } from "@/shared/schema";

const DAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function formatTime12(time: string) {
    const [h, m] = time.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function formatDateShort(dateStr: string) {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
        weekday: "short", month: "short", day: "numeric",
    });
}



const STATUS_COLORS: Record<string, string> = {
    pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    confirmed: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    completed: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    no_show: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
    ongoing: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
};

type SubTab = "list" | "settings" | "blackouts";

interface Props {
    restaurantId: string;
    mode: "admin" | "staff";
}

export function ReservationsTab({ restaurantId, mode }: Props) {
    const [subTab, setSubTab] = useState<SubTab>(mode === "admin" ? "settings" : "list");
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split("T")[0]);
    const [filterStatus, setFilterStatus] = useState("");
    const { toast } = useToast();

    // Fetch reservations
    const dateQuery = filterDate ? `&date=${filterDate}` : "";
    const statusQuery = filterStatus ? `&status=${filterStatus}` : "";
    const { data: reservations = [], isLoading: isReservationsLoading } = useQuery<ReservationWithTables[]>({
        queryKey: [`/api/restaurant/${restaurantId}/reservations?_=1${dateQuery}${statusQuery}`],
        enabled: subTab === "list",
    });

    // Fetch settings
    const { data: settings, isLoading: isSettingsLoading } = useQuery<ReservationSettings>({
        queryKey: [`/api/restaurant/${restaurantId}/reservation-settings`],
    });

    // Fetch blackout dates
    const { data: blackouts = [], isLoading: isBlackoutsLoading } = useQuery<ReservationBlackoutDate[]>({
        queryKey: [`/api/restaurant/${restaurantId}/blackout-dates`],
        enabled: subTab === "blackouts",
    });



    // Update status mutation
    const updateStatusMutation = useMutation({
        mutationFn: async ({ reservationId, status }: { reservationId: string; status: string }) => {
            const response = await apiRequest("PATCH", `/api/restaurant/${restaurantId}/reservations/${reservationId}/status`, { status });
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ predicate: (query) => (query.queryKey[0] as string).includes(`/api/restaurant/${restaurantId}/reservations`) });
            toast({ title: "Status Updated", description: "Reservation status has been updated" });
        },
        onError: (error: Error) => {
            toast({ title: "Error", description: error.message, variant: "destructive" });
        },
    });

    // Settings mutation
    const [settingsForm, setSettingsForm] = useState<Partial<ReservationSettings>>({});
    const updateSettingsMutation = useMutation({
        mutationFn: async (data: any) => {
            const response = await apiRequest("PUT", `/api/restaurant/${restaurantId}/reservation-settings`, data);
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`/api/restaurant/${restaurantId}/reservation-settings`] });
            toast({ title: "Settings Saved", description: "Reservation settings updated successfully" });
        },
        onError: (error: Error) => {
            toast({ title: "Error", description: error.message, variant: "destructive" });
        },
    });

    // Blackout mutations
    const [newBlackoutDate, setNewBlackoutDate] = useState("");
    const [newBlackoutReason, setNewBlackoutReason] = useState("");
    const addBlackoutMutation = useMutation({
        mutationFn: async (data: { blackoutDate: string; reason?: string }) => {
            const response = await apiRequest("POST", `/api/restaurant/${restaurantId}/blackout-dates`, data);
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`/api/restaurant/${restaurantId}/blackout-dates`] });
            setNewBlackoutDate("");
            setNewBlackoutReason("");
            toast({ title: "Blackout Date Added" });
        },
        onError: (error: Error) => {
            toast({ title: "Error", description: error.message, variant: "destructive" });
        },
    });

    const deleteBlackoutMutation = useMutation({
        mutationFn: async (id: string) => {
            await apiRequest("DELETE", `/api/restaurant/${restaurantId}/blackout-dates/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`/api/restaurant/${restaurantId}/blackout-dates`] });
            toast({ title: "Blackout Date Removed" });
        },
    });

    const sendEmailMutation = useMutation({
        mutationFn: async (reservationId: string) => {
            const response = await apiRequest("POST", `/api/restaurant/${restaurantId}/reservations/${reservationId}/send-confirmation-email`, {});
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Email Sent", description: "Confirmation email has been sent to the customer." });
        },
        onError: (error: Error) => {
            toast({ title: "Error", description: error.message, variant: "destructive" });
        },
    });

    // Merge settings into form
    const s = { ...settings, ...settingsForm };

    const bookingUrl = `${window.location.origin}/reserve/${restaurantId}`;

    return (
        <div className="space-y-6">
            {/* Header with booking link */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-2xl font-bold">Reservations</h2>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            navigator.clipboard.writeText(bookingUrl);
                            toast({ title: "Link Copied", description: "Booking page link copied to clipboard" });
                        }}
                    >
                        <Copy className="h-4 w-4 mr-1" /> Copy Booking Link
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                        <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4 mr-1" /> Open Booking Page
                        </a>
                    </Button>
                </div>
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-1 border-b">
                {[
                    { id: "list" as SubTab, label: "Reservations", icon: CalendarDays, show: true },
                    { id: "settings" as SubTab, label: "Settings", icon: Settings, show: mode === "admin" },
                    { id: "blackouts" as SubTab, label: "Blackout Dates", icon: Ban, show: mode === "admin" },
                ].filter(t => t.show).map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        onClick={() => setSubTab(id)}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${subTab === id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                    >
                        <Icon className="h-4 w-4" />
                        {label}
                    </button>
                ))}
            </div>

            {/* Reservations List */}
            {subTab === "list" && (
                <div className="space-y-4">
                    {/* Filters */}
                    <div className="flex flex-wrap gap-3 items-end">
                        <div>
                            <Label className="text-xs">Date</Label>
                            <Input
                                type="date"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                                className="w-44"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Status</Label>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="flex h-10 w-36 rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                <option value="">All</option>
                                <option value="pending">Pending</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="completed">Completed</option>
                                <option value="no_show">No Show</option>
                            </select>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setFilterDate(""); setFilterStatus(""); }}>
                            Clear Filters
                        </Button>
                    </div>

                    {isReservationsLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin" />
                        </div>
                    ) : reservations.length === 0 ? (
                        <Card className="p-12 text-center">
                            <CalendarDays className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                            <p className="text-muted-foreground">No reservations found</p>
                        </Card>
                    ) : (
                        <div className="space-y-3">
                            {reservations.map((r) => (
                                <Card key={r.id} className="p-4">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="space-y-1 flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold">{r.customerName}</span>
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[r.status] || ""}`}>
                                                    {r.status.replace("_", " ")}
                                                </span>
                                                <span className="text-xs text-muted-foreground font-mono">{r.confirmationCode}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                                <span className="flex items-center gap-1">
                                                    <Clock className="h-3.5 w-3.5" />
                                                    {formatTime12(r.reservationTime)} – {formatTime12(r.endTime)}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Users className="h-3.5 w-3.5" />
                                                    {r.partySize} {r.partySize === 1 ? "guest" : "guests"}
                                                </span>
                                                <span>
                                                    Tables: {r.assignedTables?.map((t) => `#${t.tableNumber}`).join(", ") || "N/A"}
                                                </span>
                                            </div>
                                            {r.customerPhone && (
                                                <p className="text-xs text-muted-foreground">📞 {r.customerPhone}</p>
                                            )}
                                            {r.specialRequests && (
                                                <p className="text-xs text-muted-foreground italic">"{r.specialRequests}"</p>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        {r.status !== "cancelled" && r.status !== "completed" && r.status !== "no_show" && (
                                            <div className="flex gap-1 flex-shrink-0">
                                                {r.status === "pending" && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="text-green-600"
                                                        onClick={() => updateStatusMutation.mutate({ reservationId: r.id, status: "confirmed" })}
                                                    >
                                                        <UserCheck className="h-3.5 w-3.5 mr-1" /> Confirm
                                                    </Button>
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => updateStatusMutation.mutate({ reservationId: r.id, status: "completed" })}
                                                >
                                                    <Check className="h-3.5 w-3.5 mr-1" /> Complete
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="text-destructive"
                                                    onClick={() => updateStatusMutation.mutate({ reservationId: r.id, status: "cancelled" })}
                                                >
                                                    <X className="h-3.5 w-3.5 mr-1" /> Cancel
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="text-orange-600"
                                                    onClick={() => updateStatusMutation.mutate({ reservationId: r.id, status: "no_show" })}
                                                >
                                                    <UserX className="h-3.5 w-3.5 mr-1" /> No-show
                                                </Button>

                                                {r.customerEmail && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="text-blue-600"
                                                        onClick={() => sendEmailMutation.mutate(r.id)}
                                                        disabled={sendEmailMutation.isPending}
                                                    >
                                                        <Mail className="h-3.5 w-3.5 mr-1" />
                                                        {sendEmailMutation.isPending ? "Sending..." : "Send Email"}
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            )}




            {/* Settings */}
            {
                subTab === "settings" && mode === "admin" && (
                    <div className="space-y-6">
                        {isSettingsLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin" />
                            </div>
                        ) : (
                            <>
                                <Card className="p-6 space-y-5">
                                    <h3 className="font-semibold text-lg">General Settings</h3>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Reservation Duration (minutes)</Label>
                                            <Input
                                                type="number"
                                                value={s.reservationDurationMinutes || 90}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, reservationDurationMinutes: parseInt(e.target.value) || 90 })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Advance Booking (days)</Label>
                                            <Input
                                                type="number"
                                                value={s.advanceBookingDays || 30}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, advanceBookingDays: parseInt(e.target.value) || 30 })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Min Party Size</Label>
                                            <Input
                                                type="number"
                                                value={s.minPartySize || 1}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, minPartySize: parseInt(e.target.value) || 1 })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Max Party Size</Label>
                                            <Input
                                                type="number"
                                                value={s.maxPartySize || 20}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, maxPartySize: parseInt(e.target.value) || 20 })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Cancellation Window (hours)</Label>
                                            <Input
                                                type="number"
                                                value={s.cancellationHours ?? 2}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, cancellationHours: parseInt(e.target.value) || 0 })}
                                            />
                                            <p className="text-xs text-muted-foreground">How many hours before the reservation can customers cancel (0 = anytime)</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-6 pt-2">
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                checked={s.autoConfirm ?? true}
                                                onCheckedChange={(v) => setSettingsForm({ ...settingsForm, autoConfirm: v })}
                                            />
                                            <Label>Auto-confirm reservations</Label>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                checked={s.allowTableCombining ?? true}
                                                onCheckedChange={(v) => setSettingsForm({ ...settingsForm, allowTableCombining: v })}
                                            />
                                            <Label>Allow table combining</Label>
                                        </div>
                                    </div>
                                </Card>

                                <Button
                                    onClick={() => updateSettingsMutation.mutate(settingsForm)}
                                    disabled={updateSettingsMutation.isPending}
                                >
                                    {updateSettingsMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                    Save Settings
                                </Button>
                            </>
                        )}
                    </div>
                )
            }

            {/* Blackout Dates */}
            {
                subTab === "blackouts" && mode === "admin" && (
                    <div className="space-y-4">
                        <Card className="p-6 space-y-4">
                            <h3 className="font-semibold">Add Blackout Date</h3>
                            <div className="flex flex-wrap gap-3 items-end">
                                <div className="space-y-1">
                                    <Label className="text-xs">Date</Label>
                                    <Input
                                        type="date"
                                        value={newBlackoutDate}
                                        onChange={(e) => setNewBlackoutDate(e.target.value)}
                                        className="w-44"
                                    />
                                </div>
                                <div className="space-y-1 flex-1 min-w-[200px]">
                                    <Label className="text-xs">Reason (optional)</Label>
                                    <Input
                                        placeholder="e.g. Private event, Holiday"
                                        value={newBlackoutReason}
                                        onChange={(e) => setNewBlackoutReason(e.target.value)}
                                    />
                                </div>
                                <Button
                                    onClick={() => addBlackoutMutation.mutate({ blackoutDate: newBlackoutDate, reason: newBlackoutReason || undefined })}
                                    disabled={!newBlackoutDate || addBlackoutMutation.isPending}
                                >
                                    <Plus className="h-4 w-4 mr-1" /> Add
                                </Button>
                            </div>
                        </Card>

                        {isBlackoutsLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin" />
                            </div>
                        ) : blackouts.length === 0 ? (
                            <Card className="p-8 text-center">
                                <Ban className="h-10 w-10 text-muted-foreground/50 mx-auto mb-2" />
                                <p className="text-muted-foreground">No blackout dates set</p>
                            </Card>
                        ) : (
                            <div className="space-y-2">
                                {blackouts.map((b) => (
                                    <Card key={b.id} className="p-4 flex items-center justify-between">
                                        <div>
                                            <span className="font-medium">{formatDateShort(b.blackoutDate)}</span>
                                            {b.reason && <span className="text-sm text-muted-foreground ml-2">— {b.reason}</span>}
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="text-destructive"
                                            onClick={() => deleteBlackoutMutation.mutate(b.id)}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>
                )
            }
        </div >
    );
}
